import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

const AmortizationGraph = ({
  totalLoan,
  ratePerPeriod,
  totalPayments,
  paymentPerPeriod,
  paymentsPerYear,
}) => {
  const [chartData, setChartData] = useState({});

  useEffect(() => {
    const pmt = parseFloat(paymentPerPeriod);
    const years = Math.ceil(totalPayments / paymentsPerYear);
    const yearlyPrincipal = [];
    const yearlyInterest = [];
    const labels = [];

    let balance = parseFloat(totalLoan);

    for (let yr = 1; yr <= years; yr++) {
      let principalThisYear = 0;
      let interestThisYear = 0;
      const paymentsThisYear = yr === years
        ? totalPayments - (yr - 1) * paymentsPerYear
        : paymentsPerYear;

      for (let p = 0; p < paymentsThisYear; p++) {
        const interestPayment = balance * ratePerPeriod;
        const principalPayment = Math.min(pmt - interestPayment, balance);
        interestThisYear += interestPayment;
        principalThisYear += principalPayment;
        balance -= principalPayment;
        if (balance < 0) balance = 0;
      }

      labels.push(yr);
      yearlyPrincipal.push(Math.round(principalThisYear));
      yearlyInterest.push(Math.round(interestThisYear));
    }

    const formatK = (v) => {
      if (v >= 1000) return '$' + (v / 1000).toFixed(0) + 'K';
      return '$' + v.toLocaleString('en-CA');
    };

    const data = {
      labels,
      datasets: [
        {
          label: 'Capital',
          data: yearlyPrincipal,
          backgroundColor: '#1E3A5F',
          hoverBackgroundColor: '#1A3152',
          borderRadius: { topLeft: 0, topRight: 0, bottomLeft: 4, bottomRight: 4 },
          borderSkipped: false,
        },
        {
          label: 'Intereses',
          data: yearlyInterest,
          backgroundColor: '#D32F2F',
          hoverBackgroundColor: '#B71C1C',
          borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
          borderSkipped: false,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: true,
          title: {
            display: true,
            text: 'Año',
            color: '#5F6B7A',
            font: { size: 12, family: 'Inter, sans-serif', weight: '600' },
          },
          ticks: {
            color: '#78909C',
            font: { size: 11, family: 'Inter, sans-serif' },
            maxTicksLimit: 15,
          },
          grid: { display: false },
        },
        y: {
          stacked: true,
          title: {
            display: true,
            text: 'Pagos anuales',
            color: '#5F6B7A',
            font: { size: 12, family: 'Inter, sans-serif', weight: '600' },
          },
          ticks: {
            callback: (value) => formatK(value),
            color: '#78909C',
            font: { size: 11, family: 'Inter, sans-serif' },
          },
          grid: { color: '#F0F0F0' },
          border: { display: false },
        },
      },
      plugins: {
        tooltip: {
          bodyFont: { family: 'Inter, sans-serif' },
          titleFont: { family: 'Inter, sans-serif', weight: 600 },
          bodyColor: '#1A1A1A',
          titleColor: '#1A1A1A',
          backgroundColor: '#FFFFFF',
          borderColor: '#E6E9EE',
          borderWidth: 1,
          cornerRadius: 8,
          padding: 12,
          callbacks: {
            title: (items) => `Año ${items[0].label}`,
            label: (context) => {
              const v = context.parsed.y;
              return ` ${context.dataset.label}: $${v.toLocaleString('en-CA')}`;
            },
            afterBody: (items) => {
              const total = items.reduce((sum, i) => sum + i.parsed.y, 0);
              return `\n  Total: $${total.toLocaleString('en-CA')}`;
            },
          },
        },
        legend: {
          position: 'bottom',
          labels: {
            font: { size: 12, family: 'Inter, sans-serif' },
            color: '#1A1A1A',
            usePointStyle: true,
            pointStyleWidth: 10,
            padding: 20,
            pointStyle: 'rectRounded',
          },
        },
      },
    };

    setChartData({ data, options });
  }, [totalLoan, ratePerPeriod, totalPayments, paymentPerPeriod, paymentsPerYear]);

  if (!chartData.data) return null;
  return <Bar data={chartData.data} options={chartData.options} />;
};

export default AmortizationGraph;
